import { RootState } from "src/context/store";
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { invoiceActions } from "src/context/invoice-slice";
import { fetchTextractData } from "src/services/invoice-api";
import { useToast } from "src/hooks/useToast";
import { Spinner } from "src/components";

const AllTextract = ({ searchValue, textract }: { searchValue?: string; textract?: string }) => {
  const { invoiceStockistTextractData } = useSelector((state: RootState) => state.invoice);
  const { InvoiceFilters } = useSelector((state: RootState) => state.invoice);
  const dispatch = useDispatch();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  // const [page, setPage] = useState(1);
  // Memoizing search parameters
  const searchParams = useMemo(() => {
    const filters = InvoiceFilters || {};
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    console.log(filters?.dateRange?.from)
    return {
      company_code: filters?.company?.map(item => item.code)?.join(",") || "",
      division_code: filters?.division?.map(item => item.company_divisioncode)?.join(",") || "",
      startDate: filters?.dateRange?.from || firstDayOfMonth,
      endDate: filters?.dateRange?.to || today,
      assignee_id: filters?.assignee?.map(item => item.invoice_assignment_id)?.join(",") || "",
      city: filters?.city?.map(item => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: filters?.district?.map(item => item.district_name)?.join(",") || "",
      state: filters?.state?.map(item => item.state_name)?.join(",") || "",
      ordering: filters?.sorting?.[0]?.key,
      textract_configured: filters?.textract?.[0]?.key,
      fuid_status_id: searchValue ? "" : filters?.status?.map(item => item.fuid_status_id)?.join(",") || "",
      page_size: 10,
      page_number: 1,
      screen: "Textract",
      dateType: filters?.dateType?.[0]?.key || "",
      stockist_medvol_code: searchValue,
      ...(textract == "All" ? {} : { textract_configured: textract })
    };
  }, [searchValue, InvoiceFilters]);
  // }, [searchValue, InvoiceFilters, page]);

  // Fetch data for the first time
  const fetchTabData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchTextractData(searchParams);
      dispatch(invoiceActions.setInvoicesStockistTextractData(response?.data?.data?.stockists || []));
      setCurrentPage(1)
      toast({ description: "Textract data fetched successfully", variant: "default" });
    } catch (error) {
      console.error("Error fetching invoices data:", error);
      toast({ description: "Failed to fetch Textract data", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, searchParams, toast]);

  useEffect(() => {
    fetchTabData();
  }, [fetchTabData]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Set how many items per page you want

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = invoiceStockistTextractData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(invoiceStockistTextractData?.length / itemsPerPage);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mt-2 w-full pb-4">
      {currentInvoices?.length === 0 && (
        <div className="flex justify-center items-center h-[20vh]">
          <p className="text-lg text-gray-500">No invoices found</p>
        </div>
      )}

      <div
        id="scrollableDiv"
        className="h-[calc(100vh-250px)] relative overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        <div className="w-full mx-auto p-6 bg-gray-50">
          <div className="space-y-4">
            {currentInvoices?.map((data: any, index: number) => (
              <div
                key={index}
                className="bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="flex justify-between w-full">
                      <span className="mr-10 text-sm font-semibold text-gray-900 flex-grow text-center">
                        Stockist Code: {data?.stockist_medvol_code}
                      </span>
                      |
                      <span className="mr-10 ml-10 text-sm font-semibold text-gray-900 flex-grow text-center">
                        Stockist Name: {data?.stockist_name}
                      </span>
                      |
                      <span className="mr-10 ml-10 text-sm font-semibold text-gray-900 flex-grow text-center">
                        Count: {data?.count}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center space-x-4 mt-4">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 bg-gray-200 rounded"
        >
          Previous
        </button>

        <span className="text-lg">
          Page {currentPage} of {totalPages}
        </span>

        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-4 py-2 bg-gray-200 rounded"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AllTextract;
